import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';
import {
  CommunicationRecipientStatus,
  MassNotificationRecipientStatItem,
  MassNotificationSeverity,
  NotificationResponseStatus
} from '../store/communications';
import { map } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { NotificationPrefsApiService } from '@app/modules/notification-prefs/store/notification-prefs-api.service';
import { RatingArea, RatingScale, RatingScaleLevel } from '@app/modules/management/store/ratings/ratings.model';
import { ColorUtilsService } from '@app/core/services';
import { UserContextService } from '@app/modules/core/services/user-context.service';
import { HttpService } from '@app/modules/core/services/http.service';
import { API_BASE_URL } from '@app/core/constants';

interface LoadedRatingScale extends RatingScale {
  organizationUId: string;
}

@Injectable({ providedIn: 'root' })
export class NotificationStatusService {
  private translate = inject(TranslateService);
  private http = inject(HttpService);
  private colorUtils = inject(ColorUtilsService);
  private userContext = inject(UserContextService).getContext;

  private recipientStatusToColor = [
    { id: CommunicationRecipientStatus.None, color: '#D6D5DD' },
    { id: CommunicationRecipientStatus.Pending, color: '#B197FC' },
    { id: CommunicationRecipientStatus.Error, color: '#E03131' },
    { id: CommunicationRecipientStatus.Delivered, color: '#228BE6' },
    { id: CommunicationRecipientStatus.Bounced, color: '#E03131' },
    { id: CommunicationRecipientStatus.Opened, color: '#FAB005' },
    { id: CommunicationRecipientStatus.Sent, color: '#FAB005' },
    { id: CommunicationRecipientStatus.Responded, color: '#74B816' }
  ];

  private recipientResponseStatusToColor = [
    { id: NotificationResponseStatus.None, color: '#D6D5DD' },
    { id: NotificationResponseStatus.Responded, color: '#FF922B' },
    { id: NotificationResponseStatus.RespondedPositive, color: '#66A80F' },
    { id: NotificationResponseStatus.RespondedNegative, color: '#C92A2A' }
  ];

  private severityItems = [
    {
      severity: MassNotificationSeverity.Low,
      backColor: '#94d82d',
      textColor: '#ffffff',
      label: this.translate.instant('COMMUNICATIONS.NOTIFICATION-SEVERITY-' + MassNotificationSeverity.Low),
      name: this.translate.instant('NOTIFICATION-PREFERENCES.LOW-HEADING'),
      description: this.translate.instant('NOTIFICATION-PREFERENCES.LOW-DETAILS')
    },
    {
      severity: MassNotificationSeverity.Moderate,
      backColor: '#ffd43b',
      textColor: '#1e1B31',
      label: this.translate.instant('COMMUNICATIONS.NOTIFICATION-SEVERITY-' + MassNotificationSeverity.Moderate),
      name: this.translate.instant('NOTIFICATION-PREFERENCES.MODERATE-HEADING'),
      description: this.translate.instant('NOTIFICATION-PREFERENCES.MODERATE-DETAILS')
    },
    {
      severity: MassNotificationSeverity.High,
      backColor: '#ff8129',
      textColor: '#ffffff',
      label: this.translate.instant('COMMUNICATIONS.NOTIFICATION-SEVERITY-' + MassNotificationSeverity.High),
      name: this.translate.instant('NOTIFICATION-PREFERENCES.HIGH-HEADING'),
      description: this.translate.instant('NOTIFICATION-PREFERENCES.HIGH-DETAILS')
    },
    {
      severity: MassNotificationSeverity.Extreme,
      backColor: '#e03131',
      textColor: '#ffffff',
      label: this.translate.instant('COMMUNICATIONS.NOTIFICATION-SEVERITY-' + MassNotificationSeverity.Extreme),
      name: this.translate.instant('NOTIFICATION-PREFERENCES.EXTREME-HEADING'),
      description: this.translate.instant('NOTIFICATION-PREFERENCES.EXTREME-DETAILS')
    }
  ];

  ratingScale: LoadedRatingScale;
  isLoading = false;

  public getDefaultSeverityItems(): {
    severity: MassNotificationSeverity;
    backColor: string;
    textColor: string;
    label: string;
    name: string;
    description: string;
  }[] {
    return this.severityItems;
  }

  public getNotificationRatingLevelItem(severity: MassNotificationSeverity): RatingScaleLevel {
    return this.ratingScale?.levels?.find(s => s.level === severity) ?? null;
  }

  public async loadNotificationRatingScale(): Promise<void> {
    const { organizationUId } = this.userContext();

    if (this.ratingScale?.organizationUId === organizationUId || this.isLoading) {
      return;
    }

    this.isLoading = true;

    const customRatingScale = await firstValueFrom(this.http.get(`${API_BASE_URL}/v1/meta/ratings/area/${RatingArea.Notification}`)).catch(err => {
      console.error(err);
      return null;
    });

    if (!!customRatingScale) {
      this.ratingScale = {
        ...customRatingScale,
        levels: customRatingScale.levels?.map(level => ({
          ...level,
          textColor: this.colorUtils.isColorLightOrDark(level.htmlColorCode) === 'light' ? '#1e1B31' : '#ffffff'
        })),
        organizationUId
      };
    } else {
      // default RatingScale
      this.ratingScale = {
        uId: uuidv4(),
        organizationUId,
        name: 'Default Notification Rating Scale',
        levels: this.severityItems.map((s, index) => ({
          uId: uuidv4(),
          displayLevel: index + 1,
          level: index + 1,
          label: s.label,
          name: s.name,
          description: s.description,
          htmlColorCode: s.backColor,
          textColor: s.textColor,
          cultureCode: this.translate.getCurrentLang()
        })),
        ratingArea: RatingArea.Notification,
        mapping: []
      };
    }

    this.isLoading = false;
  }

  unload(): void {
    this.ratingScale = null;
  }

  public getRatingTextColor(color: string): string {
    return this.colorUtils.isColorLightOrDark(color) === 'light' ? '#000000' : '#ffffff';
  }

  public getRecipientStatusColor(status: CommunicationRecipientStatus): string {
    return this.recipientStatusToColor?.find(r => r.id === status)?.color;
  }

  public getRecipientResponseStatusColor(status: NotificationResponseStatus): string {
    return this.recipientResponseStatusToColor?.find(r => r.id === status)?.color;
  }

  public getBestRecipientStatus(channelItems: MassNotificationRecipientStatItem[]): CommunicationRecipientStatus {
    if (channelItems?.length > 0 && !channelItems?.some(r => r.status !== CommunicationRecipientStatus.Error)) {
      return CommunicationRecipientStatus.Error;
    }
    return channelItems?.some(r => r.status === CommunicationRecipientStatus.Responded)
      ? CommunicationRecipientStatus.Responded
      : channelItems?.some(r => r.status === CommunicationRecipientStatus.Delivered)
        ? CommunicationRecipientStatus.Delivered
        : channelItems?.some(r => r.status === CommunicationRecipientStatus.Sent)
          ? CommunicationRecipientStatus.Sent
          : channelItems?.some(r => r.status === CommunicationRecipientStatus.Pending)
            ? CommunicationRecipientStatus.Pending
            : CommunicationRecipientStatus.None;
  }

  public getStatusKeyValues(): { [x: string]: string } {
    const keys = this.statusErrorKeys;
    const values: { [x: string]: string } = {};

    keys.forEach(v => {
      values[v] = this.translate.instant(`COMMUNICATIONS.ERRORS.${v}`);
    });

    return values;
  }

  public getStatusKeyValueDisplay(key: string) {
    return this.translate.instant(`COMMUNICATIONS.ERRORS.${key}`);
  }

  public statusErrorKeys = [
    'SYSTEM',
    'CONFIGURATION',
    'INVALID-ADDRESS',
    'BAD-STATUS-CODE',
    'BAD-DESIGN',
    'ADDRESS-NOT-FOUND',
    'MESSAGE-DROPPED',
    'MESSAGE-BOUNCED',
    'MESSAGE-DEFFERED',
    'SUBSCRIPTION-CHECK',
    'DO-NOT-DISTURB',
    'ADDRESS-WAS-REMOVED-BY-PREFS',
    'RULE-NOTIFICATION-DELETED',
    'BOT-NOT-IN-COVERSATION',
    'BOT-NOT-IN-CONVERSATION',
    'CONVERSATION-BLOCKED-BY-USER',
    'PERMISSION-ERROR',
    'NO-MOBILE-DEVICE'
  ];
}
